import React, { useState, useEffect } from "react";
import axios from "axios";
import "semantic-ui-css/semantic.min.css";
import { useAuth } from "../../../hook/userAuth";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { BASE_API } from "../../../ruta-server/BaseApi";
import { Loader, Button, Icon } from "semantic-ui-react";
import { TableAlumnosR } from "../../../components/Alumnos/ReinscripcionGrupos/TableAlumnosR";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";

function ListaAlumnosGrupo() {
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const datosGrupo = location.state?.datosGrupo;

  useEffect(() => {
    if (!datosGrupo) {
      navigate("/ReinscripcionAlumnos");
    }
  }, [datosGrupo, navigate]);

  if (!auth?.me?.es_personal_control_escolar) {
    return <Navigate to={"/"} />;
  }

  return datosGrupo ? (
    <LogicaListaAlumnosGrupo datosGrupo={datosGrupo} />
  ) : null;
}

function LogicaListaAlumnosGrupo({ datosGrupo }) {
  const { auth } = useAuth();

  const [refetch, setRefetch] = useState(false);
  const [alumnosGrupo, setAlumnosGrupo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAlumnosGrupo() {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_API}/api/grupo/`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        });

        const grupos = response.data;
        const grupo = grupos.find(
          (grupo) => grupo.id_grupo === datosGrupo.id_grupo
        );
        if (grupo) {
          const alumnosIds = grupo.alumnos;
          const alumnosResponse = await Promise.all(
            alumnosIds.map(async (alumnoId) => {
              const response = await axios.get(
                `${BASE_API}/api/alumno/${alumnoId}/`,
                {
                  headers: {
                    Authorization: `Bearer ${auth.token}`,
                  },
                }
              );
              return response.data;
            })
          );
          setAlumnosGrupo(alumnosResponse);
          setLoading(false);
        } else {
          // No se encontró el grupo
          setLoading(false);
          // Manejar el caso en el que el grupo no existe
        }
      } catch (error) {
        setLoading(false);
        console.error("Error al obtener alumnos del grupo:", error);
        // Manejar el error
      }
    }

    fetchAlumnosGrupo(); // Llamada a la función dentro del useEffect
  }, [auth.token, datosGrupo.id_grupo, refetch]);

  const onRefetch = () => {
    setRefetch((prev) => !prev);
  };

  async function verificarGrupo() {
    try {
      const response = await axios.get(`${BASE_API}/api/grupo/`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });
      const grupos = response.data;

      if (datosGrupo) {
        const nuevoNumeroGrupo = datosGrupo.numero_grupo + 100;
        return grupos.find((grupo) => grupo.numero_grupo === nuevoNumeroGrupo);
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error al verificar el nuevo grupo:", error);
      throw error;
    }
  }

  async function reinscribirAlumnos() {
    let algunAlumnoEnOctavoSemestre = false;

    // Verificar si algún alumno está en octavo semestre
    for (const alumno of alumnosGrupo) {
      if (alumno.semestre === 8) {
        algunAlumnoEnOctavoSemestre = true;
        break; // Si encontramos un alumno en octavo semestre, no necesitamos seguir iterando
      }
    }

    const confirmMessage = algunAlumnoEnOctavoSemestre
      ? `<div style="color: red;"> Los alumnos de este grupo han alcanzado el número máximo de semestres, por lo que se procederá a cambiar el estatus de los estudiantes a "egresado".
      (Todo el historial académico seguirá guardado).</div> <div>¿Deseas continuar con este proceso? </div>`
      : "Seguro que desea reinscribir a todo el grupo";

    const result = await Swal.fire({
      html: confirmMessage, // Cambiado de 'text' a 'html'
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí",
      confirmButtonColor: "#2f6d02",
      cancelButtonText: "Cancelar",
      cancelButtonColor: "#920804",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const grupoNuevo = await verificarGrupo();

        const updatedAlumnos = [];

        for (const alumno of alumnosGrupo) {
          let updatedAlumno;
          if (alumno.semestre === 8) {
            await statusEgresadoAlumno(alumno);
            updatedAlumno = { ...alumno };
          } else {
            updatedAlumno = { ...alumno, semestre: alumno.semestre + 1 };

            // Actualizar grupo solo si el semestre es 2, 4 o 6
            if ([2, 4, 6].includes(alumno.semestre)) {
              if (!grupoNuevo) {
                toast.error(
                  "El próximo grupo no existe. No se puede realizar la reinscripción."
                );
                return;
              }
              if (grupoNuevo.alumnos.length > 0) {
                toast.error(
                  `El próximo grupo ${grupoNuevo.numero_grupo} no está vacío. No se puede realizar la reinscripción.`
                );
                return;
              }
              await eliminarAlumnoDelGrupoAntiguo(alumno.id);
              await agregarAlumnoNuevoGrupo(alumno.id);
            }
          }

          // Actualizar la información del alumno en la API
          const url = `${BASE_API}/api/alumno/${alumno.id}/`;
          const headers = {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          };

          const response = await axios.patch(url, updatedAlumno, { headers });
          updatedAlumnos.push(response.data);
        }
        onRefetch();
        toast.success("Reinscripción exitosa");
        return updatedAlumnos;
      } catch (error) {
        console.error("Error al reinscribir alumnos:", error);
        toast.error("Error al reinscribir alumnos");
        throw error;
      }
    }
  }

  async function agregarAlumnoNuevoGrupo(alumnoId) {
    try {
      const grupoNuevo = await verificarGrupo();
      if (grupoNuevo) {
        const nuevaListaAlumnosIds = [
          ...grupoNuevo.alumnos.map((id) => parseInt(id)),
          parseInt(alumnoId),
        ];
        const data = {
          numero_grupo: grupoNuevo.numero_grupo,
          capacidad: grupoNuevo.capacidad,
          alumnos: nuevaListaAlumnosIds,
        };
        // Actualizar la información del nuevo grupo en la API
        const response = await axios.put(
          `${BASE_API}/api/grupo/${grupoNuevo.id_grupo}/`,
          data,
          {
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );
        return response.data;
      } else {
        //console.error("No se encontró el nuevo grupo.");
        return null;
      }
    } catch (error) {
      //console.error("Error al agregar alumno a un nuevo grupo:", error);
      throw error;
    }
  }

  async function getGrupoActualDelAlumno(alumnoId) {
    try {
      // Buscar todos los grupos
      const response = await axios.get(`${BASE_API}/api/grupo/`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });
      const grupos = response.data;

      // Buscar el grupo que contiene al alumno
      const grupoActual = grupos.find((grupo) =>
        grupo.alumnos.includes(alumnoId)
      );

      return grupoActual;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async function eliminarAlumnoDelGrupoAntiguo(alumnoId) {
    try {
      // Obtener el grupo actual del alumno
      const grupoActual = await getGrupoActualDelAlumno(alumnoId);

      // Verificar si el grupo actual está definido
      if (grupoActual && grupoActual.id_grupo) {
        // Eliminar al alumno del grupo actual
        const url = `${BASE_API}/api/grupo/${grupoActual.id_grupo}/`; // URL del grupo actual
        const headers = {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
        };

        // Filtrar al alumno que se va a eliminar del grupo
        const alumnosIdsActualizados = grupoActual.alumnos.filter(
          (id) => id !== alumnoId
        );

        // Crear el objeto de datos actualizado con los alumnos filtrados
        const data = {
          ...grupoActual,
          alumnos: alumnosIdsActualizados,
        };

        // Realizar la solicitud PUT para actualizar el grupo con los alumnos filtrados
        await axios.put(url, data, { headers });
        onRefetch();
      } else {
        console.error("No se pudo obtener el grupo actual del alumno.");
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async function statusEgresadoAlumno(data) {
    eliminarAlumnoDelGrupoAntiguo(data.id);
    try {
      const url = `${BASE_API}/api/alumno/${data.id}/`;
      const headers = {
        Authorization: `Bearer ${auth.token}`,
        "Content-Type": "application/json",
      };

      // Crear un objeto con solo el atributo estatus
      const newData = {
        estado_alumno: (data.estado_alumno = "Egresado"),
      };

      const response = await axios.patch(url, newData, { headers });

      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        if (error.response.data.numero_grupo !== undefined) {
          toast.error(
            "campo de descripción: " + error.response.data.numero_grupo[0]
          );
        }
      }
      throw error;
    }
  }

  return (
    <div>
      <h1 className="tituloin">GRUPO {datosGrupo.numero_grupo}</h1>
      <h6 className="sub">
        <Link to="/ReinscripcionAlumnos">Reinscripción por grupo</Link>/ Grupo{" "}
        {datosGrupo.numero_grupo}{" "}
      </h6>

      <Button
        style={{ backgroundColor: "#007c1d", color: "white" }}
        onClick={() => {
          reinscribirAlumnos();
        }}
      >
        <Icon name="sync" style={{ color: "white" }}></Icon>
        Reinscribir alumnos
      </Button>

      {loading ? (
        <Loader active inline="centered">
          Cargando....
        </Loader>
      ) : (
        <TableAlumnosR
          alumnos={alumnosGrupo}
          grupo={datosGrupo}
          bajaTemporalAlmuno={statusEgresadoAlumno}
        />
      )}

      <ToastContainer />
    </div>
  );
}

export default ListaAlumnosGrupo;
